import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { site, dialogs, i18next, readOnly } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import useCategoryLoadingBySlug from 'src/js/modules/ai/hooks/use-category-loading-by-slug';
import websiteSettings from '../../website-settings';
import { saveSiteWithEffect } from '../../saving/helpers/save-site';
import getUnsplashSearchQuery from '../helpers/get-unsplash-search-query';
import AiOnboardingDialog from '../components/ai-onboarding-dialog';
import segment from '../../analytics/segment';
import constants from '../constants';

const { useForm } = reactHookForm;
const {
  constants: { events },
  track,
} = segment;
const {
  sourceIds,
  siteProperties: { UNSPLASH_SEARCH_QUERY_PROPERTY_NAME },
} = constants;
const {
  constants: { settingTypes, settingLengthLimits },
  helpers: { getFieldValuesFromSettings, getSettingValueFromFieldValue },
} = websiteSettings;
const { SITE_NAME, BUSINESS_NAME, BUSINESS_DESCRIPTION, BUSINESS_CATEGORY } = settingTypes;

const getCaptions = () => ({
  description: i18next.t(
    'To create customized content for your business, enter your business details.'
  ),
  descriptionWithBusinessInfo: i18next.t(
    'You can update this information at any time under {pathToBusinessInfo}.',
    {
      pathToBusinessInfo: `<strong>${i18next.t('Settings')} &#x2192; ${i18next.t(
        'Business Info'
      )}</strong>`,
    }
  ),
  buttonLabel: i18next.t('Submit & Generate'),
});

const getFields = ({ control, siteName }) => ({
  businessName: {
    label: i18next.t('Business name'),
    checkboxLabel: i18next.t('Same as website name'),
    name: BUSINESS_NAME,
    placeholder: siteName,
    control,
    rules: {
      required: i18next.t('This field is required. Please enter your business name'),
    },
  },
  businessDescription: {
    label: i18next.t('What does your business do?'),
    name: BUSINESS_DESCRIPTION,
    placeholder: i18next.t(
      'Alex & Johnson - is a real estate investment and development company based in Georgia. We specialize in acquiring, managing, and maximizing the value of residential properties.'
    ),
    statusText: i18next.t('Describe your business or project in a sentence or two.'),
    control,
    rules: {
      required: i18next.t('This field is required. Please enter a description of your business'),
      minLength: {
        value: settingLengthLimits.MIN_BUSINESS_DESCRIPTION_CHARS,
        message: i18next.t(`Please provide a description with {number}+ characters`, {
          number: settingLengthLimits.MIN_BUSINESS_DESCRIPTION_CHARS,
        }),
      },
      maxLength: {
        value: settingLengthLimits.MAX_BUSINESS_DESCRIPTION_CHARS,
        message: i18next.t(`Maximum number of characters {number}`, {
          number: settingLengthLimits.MAX_BUSINESS_DESCRIPTION_CHARS,
        }),
      },
    },
  },
  businessCategory: {
    label: i18next.t('Business category'),
    placeholder: i18next.t('E.g. Cleaning Company'),
    noResultsText: i18next.t('No result'),
    name: BUSINESS_CATEGORY,
    control,
    rules: {
      required: i18next.t('This field is required. Please select a category of your business'),
    },
  },
});

const AiOnboardingContainer = ({
  sourceId,
  title,
  onDialogMainAction,
  onDialogCancel,
  onBeforeSave = Function.prototype,
}) => {
  const dispatch = useDispatch();
  const siteSettings = useSelector(site.selectors.getSettings);
  const isReadOnlyModeEnabled = useSelector(readOnly.selectors.getReadOnlyStatus);
  const [featureFlags] = useFeatureFlags([
    'business_info_in_website_settings',
    'ai_unsplash_search_query',
  ]);
  const {
    business_info_in_website_settings: isBusinessInfoInWebsiteSettingsEnabled,
    ai_unsplash_search_query: isAiUnsplashSearchQueryEnabled,
  } = featureFlags;

  const settingFieldValues = getFieldValuesFromSettings(siteSettings);
  const defaultValues = {
    [BUSINESS_NAME]: settingFieldValues[BUSINESS_NAME] || settingFieldValues[SITE_NAME],
    [BUSINESS_DESCRIPTION]: settingFieldValues[BUSINESS_DESCRIPTION],
    [BUSINESS_CATEGORY]: settingFieldValues[BUSINESS_CATEGORY]
      ? {
          value: settingFieldValues[BUSINESS_CATEGORY],
        }
      : null,
  };
  const { control, formState, trigger, getValues, setValue } = useForm({
    defaultValues,
    mode: 'onTouched',
  });
  const { isValid, dirtyFields } = formState;
  const siteId = site.accessors.getSiteId();
  const fields = getFields({
    control,
    siteName: settingFieldValues[SITE_NAME],
  });

  const handleBusinessCategoryChange = (option) => {
    setValue(BUSINESS_CATEGORY, option);
    track(events.BUSINESS_CATEGORY_SELECTED, {
      siteId,
      stepId: null,
      businessCategory: option.value,
      archetypeId: option.archetype,
      source: sourceIds.AI_ONBOARDING,
    });
  };

  useCategoryLoadingBySlug(settingFieldValues[BUSINESS_CATEGORY], (data) => {
    setValue(BUSINESS_CATEGORY, {
      label: data.title,
      value: data.category,
      archetype: data.archetypes[0],
    });
  });

  const [isBusinessNameSameAsSiteName, setIsBusinessNameSameAsSiteName] = useState(
    defaultValues[BUSINESS_NAME] === settingFieldValues[SITE_NAME]
  );

  const captions = getCaptions();
  const description = isBusinessInfoInWebsiteSettingsEnabled
    ? `${captions.description} ${captions.descriptionWithBusinessInfo}`
    : captions.description;

  const handleBusinessCategoryFocus = () => {
    const businessCategory = getValues(BUSINESS_CATEGORY) && getValues(BUSINESS_CATEGORY).value;

    track(events.BUSINESS_CATEGORY_INPUT_FOCUSED, {
      siteId,
      stepId: null,
      source: sourceIds.AI_ONBOARDING,
      businessCategory: businessCategory || null,
    });
  };

  const handleSubmit = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const businessName = getValues(BUSINESS_NAME);
    const businessDescription = getValues(BUSINESS_DESCRIPTION);
    const selectedBusinessCategory = getValues(BUSINESS_CATEGORY);
    const businessCategory = selectedBusinessCategory && selectedBusinessCategory.value;
    const updatedFields = [
      ...(settingFieldValues[BUSINESS_NAME] !== businessName ? [BUSINESS_NAME] : []),
      ...Object.keys(dirtyFields),
    ];
    const settingsToUpdate = {};

    if (updatedFields.includes(BUSINESS_NAME)) {
      settingsToUpdate[BUSINESS_NAME] = getSettingValueFromFieldValue(BUSINESS_NAME, businessName);
    }

    if (updatedFields.includes(BUSINESS_DESCRIPTION)) {
      settingsToUpdate[BUSINESS_DESCRIPTION] = getSettingValueFromFieldValue(
        BUSINESS_DESCRIPTION,
        businessDescription
      );
    }

    if (updatedFields.includes(BUSINESS_CATEGORY)) {
      settingsToUpdate[BUSINESS_CATEGORY] = getSettingValueFromFieldValue(
        BUSINESS_CATEGORY,
        selectedBusinessCategory
      );
    }

    if (Object.keys(settingsToUpdate).length) {
      dispatch(site.actions.updateSettings(settingsToUpdate, true, true));
      if (isReadOnlyModeEnabled) return;
      dialogs.operations.hide();

      onBeforeSave();

      saveSiteWithEffect(async () => {
        if (isAiUnsplashSearchQueryEnabled && updatedFields.includes(BUSINESS_DESCRIPTION)) {
          const searchQuery = await getUnsplashSearchQuery();
          if (searchQuery) {
            dispatch(
              site.actions.setSiteProperty(UNSPLASH_SEARCH_QUERY_PROPERTY_NAME, searchQuery)
            );
          }
        }

        onDialogMainAction({
          businessName,
          businessDescription,
          sourceId,
          businessCategory,
        });
      });
      return;
    }

    onDialogMainAction({
      businessName,
      businessDescription,
      sourceId,
      businessCategory,
    });
  };

  const handleClose = () => {
    onDialogCancel({
      sourceId,
    });
    dialogs.operations.hide();
  };

  const handleBusinessNameChange = (value) => {
    if (value !== settingFieldValues[SITE_NAME]) {
      setIsBusinessNameSameAsSiteName(false);
    }
  };

  const handleBusinessNameAsSiteNameChange = (value) => {
    if (value) {
      setValue(BUSINESS_NAME, settingFieldValues[SITE_NAME], { shouldValidate: true });
    }

    setIsBusinessNameSameAsSiteName(value);
  };

  return (
    <AiOnboardingDialog
      fields={fields}
      captions={{
        title,
        description,
        buttonLabel: captions.buttonLabel,
      }}
      isBusinessNameSameAsSiteName={isBusinessNameSameAsSiteName}
      onBusinessNameChange={handleBusinessNameChange}
      onBusinessCategoryChange={handleBusinessCategoryChange}
      onBusinessCategoryFocus={handleBusinessCategoryFocus}
      onSameAsSiteNameChange={handleBusinessNameAsSiteNameChange}
      onSubmit={handleSubmit}
      onClose={handleClose}
    />
  );
};

AiOnboardingContainer.propTypes = {
  sourceId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onBeforeSave: PropTypes.func,
};

export default AiOnboardingContainer;
